import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const REASON_CONFIG = {
  'no-toc-mapping': {
    label: 'No ToC mapping',
    color: 'bg-gray-100 text-gray-700 border-gray-200',
    description: 'The result was reported under this program but was not linked to a HLO/IOC.',
  },
  'toc-no-aow-match': {
    label: 'No AoW match',
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    description: 'There is information mapping to the ToC, but the result does not match any of the AoWs (e.g. could be linked to 2030 outcomes.)',
  },
  'non-standard-type': {
    label: 'Non-standard type',
    color: 'bg-red-100 text-red-700 border-red-200',
    description: 'The result has a type that does not correspond to any standard CGIAR indicator category.',
  },
  'not-in-toc-pool': {
    label: 'Unconfirmed contribution',
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    description: 'The result was submitted by another Program/Accelerator, and its contribution to the ToC has not been confirmed by the P/A to which it would contribute.',
  },
};

const PAGE_SIZE = 25;

export function UnmatchedResultsModal({ results, totalSidebar, totalInTable, onClose }) {
  const [search, setSearch] = useState('');
  const [reasonFilter, setReasonFilter] = useState(null);
  const [page, setPage] = useState(0);

  const handleKeyDown = useCallback((e) => { if (e.key === 'Escape') onClose(); }, [onClose]);
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handleKeyDown); document.body.style.overflow = ''; };
  }, [handleKeyDown]);

  const reasonCounts = useMemo(() => {
    const counts = {};
    for (const r of results) {
      counts[r.reason] = (counts[r.reason] || 0) + 1;
    }
    return counts;
  }, [results]);

  const filtered = useMemo(() => {
    let list = results;
    if (reasonFilter) list = list.filter(r => r.reason === reasonFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r => {
        const res = r.result;
        if (!res) return false;
        return (res.resultCode?.toLowerCase().includes(q)) ||
               (res.title?.toLowerCase().includes(q)) ||
               (res.type?.toLowerCase().includes(q)) ||
               (res.initiative?.toLowerCase().includes(q));
      });
    }
    return list;
  }, [results, reasonFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageResults = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [search, reasonFilter]);

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 flex flex-col w-full max-w-5xl max-h-[85vh] bg-[var(--surface-primary)] rounded-xl shadow-2xl overflow-hidden mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--border-primary)]">
          <div>
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">
              Results Not Mapped to Areas of Work ({results.length})
            </h3>
            <p className="text-xs text-[var(--text-tertiary)] mt-0.5">
              {totalSidebar} total results — {totalInTable} mapped to AoWs — {results.length} unaccounted
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--surface-hover)] text-[var(--text-tertiary)]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-5 py-3 border-b border-[var(--border-primary)] flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--text-tertiary)]" />
            <input
              type="text"
              placeholder="Search by code, title, type..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-[var(--border-primary)] rounded-lg bg-[var(--surface-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-cgiar-green"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <button
              onClick={() => setReasonFilter(null)}
              className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${!reasonFilter ? 'bg-gray-800 text-white border-gray-800' : 'bg-[var(--surface-secondary)] text-[var(--text-secondary)] border-[var(--border-primary)] hover:bg-[var(--surface-hover)]'}`}
            >
              All ({results.length})
            </button>
            {Object.entries(REASON_CONFIG).map(([key, cfg]) => {
              const count = reasonCounts[key] || 0;
              if (count === 0) return null;
              return (
                <button
                  key={key}
                  onClick={() => setReasonFilter(reasonFilter === key ? null : key)}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${reasonFilter === key ? 'bg-gray-800 text-white border-gray-800' : `${cfg.color} hover:opacity-80`}`}
                >
                  {cfg.label} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Category descriptions */}
        <div className="px-5 py-2.5 border-b border-[var(--border-primary)] bg-[var(--surface-secondary)]">
          {reasonFilter ? (
            <div className="flex items-start gap-2 text-xs text-[var(--text-secondary)]">
              <span className={`inline-block px-2 py-0.5 text-[10px] font-medium rounded-full border flex-shrink-0 ${REASON_CONFIG[reasonFilter].color}`}>
                {REASON_CONFIG[reasonFilter].label}
              </span>
              <span>{REASON_CONFIG[reasonFilter].description}</span>
            </div>
          ) : (
            <div className="space-y-1.5">
              {Object.entries(REASON_CONFIG).map(([key, cfg]) => {
                if (!(reasonCounts[key] > 0)) return null;
                return (
                  <div key={key} className="flex items-start gap-2 text-xs text-[var(--text-secondary)]">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-medium rounded-full border flex-shrink-0 ${cfg.color}`}>
                      {cfg.label}
                    </span>
                    <span>{cfg.description}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-xs">
            <thead className="bg-[var(--surface-secondary)] border-b border-[var(--border-primary)] sticky top-0">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium text-[var(--text-secondary)]">Result Code</th>
                <th className="text-left px-3 py-2.5 font-medium text-[var(--text-secondary)]">Title</th>
                <th className="text-left px-3 py-2.5 font-medium text-[var(--text-secondary)]">Type</th>
                <th className="text-left px-3 py-2.5 font-medium text-[var(--text-secondary)]">Funding</th>
                <th className="text-left px-3 py-2.5 font-medium text-[var(--text-secondary)]">Initiative</th>
                <th className="text-left px-4 py-2.5 font-medium text-[var(--text-secondary)]">Reason</th>
              </tr>
            </thead>
            <tbody>
              {pageResults.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-[var(--text-tertiary)]">
                    {search || reasonFilter ? 'No results match your filters' : 'No unmatched results'}
                  </td>
                </tr>
              )}
              {pageResults.map((item, idx) => {
                const r = item.result;
                const cfg = REASON_CONFIG[item.reason] || REASON_CONFIG['no-toc-mapping'];
                return (
                  <tr key={idx} className="border-t border-[var(--border-primary)] hover:bg-[var(--surface-hover)]">
                    <td className="px-4 py-2 font-mono text-[var(--text-secondary)]">{r?.resultCode || '—'}</td>
                    <td className="px-3 py-2 text-[var(--text-primary)] max-w-[300px] truncate" title={r?.title}>{r?.title || '—'}</td>
                    <td className="px-3 py-2 text-[var(--text-secondary)] whitespace-nowrap">{r?.type || '—'}</td>
                    <td className="px-3 py-2 text-[var(--text-secondary)] whitespace-nowrap">{r?.fundingType || '—'}</td>
                    <td className="px-3 py-2 text-[var(--text-secondary)] whitespace-nowrap">{r?.initiative || '—'}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-0.5 text-[10px] font-medium rounded-full border ${cfg.color}`}>
                        {cfg.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-2.5 border-t border-[var(--border-primary)] bg-[var(--surface-secondary)]">
            <span className="text-xs text-[var(--text-tertiary)]">
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="p-1 rounded hover:bg-[var(--surface-hover)] text-[var(--text-tertiary)] disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-[var(--text-secondary)] px-2">
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="p-1 rounded hover:bg-[var(--surface-hover)] text-[var(--text-tertiary)] disabled:opacity-30"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
